import React, { useCallback, useEffect, useRef, useState, memo, ReactNode, PropsWithChildren } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Breakpoints } from '@settings';
import classNames from 'classnames';

type Props = React.HTMLAttributes<HTMLDivElement> & {
  children?: ReactNode;
  className?: string;
};

const StickySection = ({ children, className = '', ...props }: PropsWithChildren<Props>) => {
  const wrapperRef = useRef(null);
  const childrenRef = useRef(null);

  const isMobView = useMediaQuery({ maxWidth: Breakpoints.MD });

  const [styles, setStyles] = useState(null);

  const handleScroll = useCallback((e) => {
    window.requestAnimationFrame(() => {
      if (!wrapperRef.current || !childrenRef.current) return;

      const offset = ['mainMenu', 'SectionsNav'].reduce((a, id) => {
        const e = document.getElementById(id);
        const height = !!e ? e.getBoundingClientRect().height : 0;
        return a + height;
      }, 0);

      const wbb = wrapperRef.current.getBoundingClientRect();
      const cbb = childrenRef.current.getBoundingClientRect();

      const top = Math.max(0, Math.min(offset - wbb.top, wbb.height - cbb.height));

      setStyles({
        parent: { minHeight: cbb.height + 'px', position: 'relative' },
        children: { maxWidth: wbb.width + 'px', position: 'absolute', top: top + 'px' },
      });
    });
  }, []);

  useEffect(() => {
    if (!isMobView) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', handleScroll, { passive: true });
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isMobView]);

  const parentStyle = (!isMobView && styles?.parent) || {};
  const childrenStyle = (!isMobView && styles?.children) || {};

  return (
    <div ref={wrapperRef} {...props} className={classNames('tw-w-full', className)} style={parentStyle}>
      <div ref={childrenRef} className='tw-w-full' style={childrenStyle}>
        {children}
      </div>
    </div>
  );
};

export default memo(StickySection);
